import React, {useMemo, useState} from "react";
import Grid from "@material-ui/core/Grid";
import {StyledInput} from "../../form/input";
import {StyledButton} from "../../ui-base/button";
import ClearIcon from "@mui/icons-material/Clear";
import Menu from "@material-ui/core/Menu";
import {StyledRadioButton} from "../../form/radio";
import {useTranslation} from "react-i18next";
import styled from "styled-components";
import {StyledSelect} from "../../form/select";
import {AutocompleteSelectApi} from "../../form/autocomplete-api";
import {Form, Formik, useFormik} from "formik";
import {SearchFilterService} from "../../../services/search-filter.service";
import {FilterCategoryModel} from "../../../models/filter-category.model";
import {FilterPublisherModel} from "../../../models/filter-publisher.model";
import {FilterTagModel} from "../../../models/filter-tag.model";
import {LanguageAutocomplete} from "../../form/language-autocomplete";
import {CountryAutocomplete} from "../../form/country-autocomplete";
import {CountryModel} from "../../../models/country.model";
import {LangModel} from "../../../models/lang.model";
import {MultipleSelect} from "../../form/multiple-select";
import {FilterAlt} from "@mui/icons-material";
import {ImportExport} from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import {IconButton} from "@mui/material";

export enum ElasticSearchType {
    searchTypeTitle = 'searchTypeTitle',
    searchTypeAuthor = 'searchTypeAuthor',
    searchTypeTag = 'searchTypeTag',
    searchTypeIsbn = 'searchTypeIsbn',
    searchTypePublisher = 'searchTypePublisher',
    searchTypeCountry = 'searchTypeCountry'
}

export enum ElasticSearchSort {
    created = 'created',
    status = 'status',
    matching = 'matching',
    publishDate = 'publishDate',
    submittedDate = "submittedDate"
}

export enum ElasticFilterType {
    categories = 'categories',
    publishers = 'publishers',
    tags = 'tags',
    languages = 'languages',
    countries = 'countries',
    bookPhases = 'book_phases'
}

export const SearchForElastic: React.FC<Props> = ({loadData, showSort, showFilter, sortTypeOptions, searchTypeOptions, filterOptions}) => {
    const [sortValue, setSortValue] = useState<'asc'|'desc'>('desc');
    const [sortFieldValue, setSortFieldValue] = useState<ElasticSearchSort>(sortTypeOptions ? sortTypeOptions[0] : ElasticSearchSort.created);
    const [searchType, setSearchType] = useState<ElasticSearchType>(searchTypeOptions ? searchTypeOptions[0] : ElasticSearchType.searchTypeTitle);
    const [searchValue, setSearchValue] = useState('');
    const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);
    const { t } = useTranslation(['publisher', 'common']);

    const form = useFormik({
        initialValues: {
            categories: [] as FilterCategoryModel[],
            publishers: [] as FilterPublisherModel[],
            tags: [] as FilterTagModel[],
            languages: [] as LangModel[],
            countries: [] as CountryModel[],
            bookPhases: [] as string[]
        },
        onSubmit: () => {
            searchSubmitted();
        }
    });

    const getFilters = (): SearchFilters => {
        return {
            categories: form.values.categories.map(category => category.code),
            publishers: form.values.publishers.map(publisher => publisher.id),
            tags: form.values.tags.map(tag => tag.id),
            languages: form.values.languages.map(lang => lang.code),
            countries: form.values.countries.map(country => country.iso),
            bookPhases: form.values.bookPhases.map(bookPhase => JSON.parse(bookPhase).value)
        };
    }

    const searchChanged = (event) => {
        setSearchValue(event.target.value);

        if (!event.target.value) {
            loadData(sortFieldValue, sortValue, searchType, '', getFilters());
        }
    }

    const searchTypeChanged = (event) => {
        setSearchType(event.target.value);
        setSearchValue('');
    }

    const searchSubmitted = () => {
        loadData(sortFieldValue, sortValue, searchType, searchValue, getFilters());
    }

    const clearSearch = () => {
        setSearchValue('');
        loadData(sortFieldValue, sortValue, searchType, '', getFilters());
    }

    const handleSortClick = (event) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortClose = (event) => {
        setSortAnchorEl(null);
    };

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = (event) => {
        setFilterAnchorEl(null);
    };

    const sortFieldChanged = (event) => {
        setSortFieldValue(event.target.value);
        loadData(event.target.value, sortValue, searchType, searchValue, getFilters());
    }

    const sortChanged = (event) => {
        setSortValue(event.target.value);
        loadData(sortFieldValue, event.target.value, searchType, searchValue, getFilters());
    }

    const getSearchTypeOptions = () => {
        if (searchTypeOptions) {
            return searchTypeOptions;
        } else {
            return [ElasticSearchType.searchTypeTitle, ElasticSearchType.searchTypePublisher, ElasticSearchType.searchTypeAuthor, ElasticSearchType.searchTypeTag, ElasticSearchType.searchTypeIsbn, ElasticSearchType.searchTypeCountry];
        }
    }

    const getSearchSortOptions = () => {
        if (sortTypeOptions) {
            return sortTypeOptions.map(sortType => {
                return {label: t('searchBooksComponent.' + sortType + 'Sort'), value: sortType};
            });
        } else {
            return [
                {label: t('searchBooksComponent.' + ElasticSearchSort.matching + 'Sort'), value: ElasticSearchSort.matching},
                {label: t('searchBooksComponent.' + ElasticSearchSort.publishDate + 'Sort'), value: ElasticSearchSort.publishDate},
                {label: t('searchBooksComponent.' + ElasticSearchSort.status + 'Sort'), value: ElasticSearchSort.status},
                {label: t('searchBooksComponent.' + ElasticSearchSort.created + 'Sort'), value: ElasticSearchSort.created},
                {label: t('searchBooksComponent.' + ElasticSearchSort.submittedDate + 'Sort'), value: ElasticSearchSort.submittedDate}
            ];
        }
    }

    const getFilterOptions = () => {
        if (filterOptions) {
            return filterOptions;
        } else {
            return [ElasticFilterType.categories, ElasticFilterType.tags, ElasticFilterType.countries, ElasticFilterType.languages, ElasticFilterType.publishers]
        }
    }

    const getBookPhaseOptions = () => {
        return [{
            label: t('addBookNew.form.bookStatusLabel1'),
            value: 'CREATE'
        }, {
            label: t('addBookNew.form.bookStatusLabel2'),
            value: 'PUBLISH'
        }, {
            label: t('addBookNew.form.bookStatusLabel3'),
            value: 'MARKET'
        }];
    }

    const filterIsActive = () => {
        const filters = getFilters();
        return filters.bookPhases.length > 0 ||
            filters.tags.length > 0 ||
            filters.countries.length > 0 ||
            filters.tags.length > 0 ||
            filters.categories.length > 0 ||
            filters.languages.length > 0 ||
            filters.publishers.length > 0;
    }

    return (
        <Grid container direction={"row"} alignItems={"center"} spacing={1}>
            <Grid item xs="auto" style={{marginTop: '-6px'}}>
                <StyledSelect
                    id={'searchType'}
                    label=""
                    onChange={searchTypeChanged}
                    value={searchType}
                    showEmpty={false}
                    options={getSearchTypeOptions()}
                    optionsLabel={(option) => t('searchBooksComponent.' + option)}
                />

            </Grid>
            <Grid item xs>
                <StyledInput
                    id="search-field"
                    label=""
                    placeholder={t('searchBooksComponent.searchNewField')}
                    onChange={searchChanged}
                    value={searchValue}
                    showClear={true}
                />
            </Grid>

            {showFilter &&
                <Grid item>
                    <StyledButton onClick={handleFilterClick} type={"button"} color={"transparent"}>
                        <FilterAlt style={{color: filterIsActive() ? '#FF9C00' : 'rgba(0, 0, 0, 0.5)'}}/>
                    </StyledButton>
                    <Menu
                        id="filter-menu"
                        anchorEl={filterAnchorEl}
                        keepMounted
                        open={Boolean(filterAnchorEl)}
                        onClose={handleFilterClose}
                    >
                        <StyledCloseContainer>
                            <IconButton onClick={() => {handleFilterClose('')}}>
                                <CloseIcon/>
                            </IconButton>
                        </StyledCloseContainer>

                        <StyledFormContent onSubmit={form.handleSubmit} id="add-manuscript-form">

                            <Grid container direction={'column'} spacing={3}>
                                {getFilterOptions().includes(ElasticFilterType.categories) &&
                                    <Grid item>
                                        <AutocompleteSelectApi
                                            values={form.values.categories}
                                            multiple={true}
                                            setFieldValue={(fieldName: string, value: any) => {
                                                form.setFieldValue(fieldName, value);
                                                setTimeout(form.submitForm, 1);
                                            }}
                                            fieldName={'categories'}
                                            label={t('searchBooksComponent.categoriesFilter')}
                                            error={false}
                                            loadData={SearchFilterService.getCategories}
                                            getOptionLabel={(option: FilterCategoryModel) => option.original_name}
                                            searchInputPlaceholder={t('searchBooksComponent.categoriesFilterPlaceholder')}
                                        />
                                    </Grid>
                                }
                                {getFilterOptions().includes(ElasticFilterType.publishers) &&
                                    <Grid item>
                                        <AutocompleteSelectApi
                                            values={form.values.publishers}
                                            multiple={true}
                                            setFieldValue={(fieldName: string, value: any) => {
                                                form.setFieldValue(fieldName, value);
                                                setTimeout(form.submitForm, 1);
                                            }}
                                            fieldName={'publishers'}
                                            label={t('searchBooksComponent.publishersFilter')}
                                            error={false}
                                            loadData={SearchFilterService.getPublishers}
                                            getOptionLabel={(option: FilterPublisherModel) => option.name || ''}
                                            searchInputPlaceholder={t('searchBooksComponent.publishersFilterPlaceholder')}
                                        />
                                    </Grid>
                                }
                                {getFilterOptions().includes(ElasticFilterType.tags) &&
                                    <Grid item>
                                        <AutocompleteSelectApi
                                            values={form.values.tags}
                                            multiple={true}
                                            setFieldValue={(fieldName: string, value: any) => {
                                                form.setFieldValue(fieldName, value);
                                                setTimeout(form.submitForm, 1);
                                            }}
                                            fieldName={'tags'}
                                            label={t('searchBooksComponent.tagsFilter')}
                                            error={false}
                                            loadData={SearchFilterService.getTags}
                                            getOptionLabel={(option: FilterTagModel) => option.name}
                                            searchInputPlaceholder={t('searchBooksComponent.tagsFilterPlaceholder')}
                                        />
                                    </Grid>
                                }
                                {getFilterOptions().includes(ElasticFilterType.languages) &&
                                    <Grid item>
                                        <LanguageAutocomplete
                                            value={form.values.languages}
                                            setFieldValue={(fieldName: string, value: any) => {
                                                form.setFieldValue(fieldName, value);
                                                setTimeout(form.submitForm, 1);
                                            }}
                                            fieldName={'languages'}
                                            label={t('searchBooksComponent.languageFilter')}
                                            searchInputPlaceholder={t('searchBooksComponent.languageFilterPlaceholder')}
                                            handleChange={form.handleChange}
                                            error={false}
                                        />
                                    </Grid>
                                }
                                {getFilterOptions().includes(ElasticFilterType.countries) &&
                                    <Grid item>
                                        <CountryAutocomplete
                                            values={form.values.countries}
                                            setFieldValue={(fieldName: string, value: any) => {
                                                form.setFieldValue(fieldName, value);
                                                setTimeout(form.submitForm, 1);
                                            }}
                                            fieldName={'countries'}
                                            label={t('searchBooksComponent.countriesFilter')}
                                            searchInputPlaceholder={t('searchBooksComponent.countriesFilterPlaceholder')}
                                            error={false}
                                        />
                                    </Grid>
                                }
                                {getFilterOptions().includes(ElasticFilterType.bookPhases) &&
                                    <Grid item>
                                        <MultipleSelect
                                            fieldName={'bookPhases'}
                                            label={t('searchBooksComponent.bookPhasesFilter')}
                                            setFieldValue={(fieldName: string, value: any) => {
                                                form.setFieldValue(fieldName, value);
                                                setTimeout(form.submitForm, 1);
                                            }}
                                            value={form.values.bookPhases}
                                            options={getBookPhaseOptions()}
                                            optionsLabel={(option) => option.label}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </StyledFormContent>
                    </Menu>
                </Grid>
            }

            {showSort &&
                <Grid item>
                    <StyledButton onClick={handleSortClick} type={"button"} color={"transparent"}>
                        <ImportExport style={{color: 'rgba(0, 0, 0, 0.5)'}}/>
                    </StyledButton>
                    <Menu
                        id="sort-menu"
                        anchorEl={sortAnchorEl}
                        keepMounted
                        open={Boolean(sortAnchorEl)}
                        onClose={handleSortClose}
                    >
                        <StyledMenuContent>
                            <StyledRadioButton
                                options={getSearchSortOptions()}
                                value={sortFieldValue}
                                onChange={sortFieldChanged}
                            />
                            <hr/>
                            <StyledRadioButton
                                options={[{label: t('searchBooksComponent.desc'), value: 'desc'}, {label: t('searchBooksComponent.asc'), value: 'asc'}]}
                                value={sortValue}
                                onChange={sortChanged}
                            />
                        </StyledMenuContent>
                    </Menu>
                </Grid>
            }

            {/*<Grid item>*/}
            {/*    <StyledButton type={"button"} color={'transparent'} onClick={clearSearch}>*/}
            {/*        <ClearIcon />*/}
            {/*    </StyledButton>*/}
            {/*</Grid>*/}

            <Grid item>
                <StyledButton type={'button'} color={'violet'} onClick={searchSubmitted}>
                    {t('searchBooksComponent.searchButton')}
                </StyledButton>
            </Grid>
        </Grid>
    );
}

export interface Props {
    loadData: (
        sortFieldTmp: ElasticSearchSort,
        sortValueTmp: 'asc'|'desc',
        searchTypeValueTmp: ElasticSearchType,
        searchValueTmp: string,
        filters: SearchFilters
    ) => void,
    sortTypeOptions?: ElasticSearchSort[],
    searchTypeOptions?: ElasticSearchType[],
    showSort?: boolean,
    showFilter?: boolean,
    filterOptions?: ElasticFilterType[]
}

export interface SearchFilters {
    categories: string[],
    publishers: string[],
    tags: string[],
    languages: string[],
    countries: string[],
    bookPhases: string[]
}

const StyledMenuContent = styled('div')`
  padding: 0 1rem;
`;

const StyledFormContent = styled('form')`
  position: relative;
  padding: 0 0.5rem;
  width: 400px;
`;

const StyledCloseContainer = styled('div')`
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  z-index:1;
`;
